import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { EngineeringGrid } from "@/components/fina/EngineeringGrid";

const IMG_WELD = "/brand/capability-welding.jpg";
const IMG_CAD = "/brand/capability-cad.jpg";
const IMG_FAB = "/brand/capability-fabrication.jpg";
const IMG_QA =
  "https://static.prod-images.emergentagent.com/jobs/5102b00f-8bc4-4859-8124-35bac0e1cdac/images/3aac0bcb6d1ad7a73cb4da25f60ff51b27c0441c175d37d0893e536cd40b7de5.png";

const steps = [
  {
    id: 1,
    tag: "01 — Welding",
    title: "TIG-perfect seams, traceable beads.",
    body: "Coded TIG welders delivering clean, hygienic, crevice-free joints in 304 & 316L stainless — built for demanding food and pharmaceutical environments.",
    img: IMG_WELD,
  },
  {
    id: 2,
    tag: "02 — Fabrication",
    title: "From sheet to structure.",
    body: "Laser cutting, forming and precision assembly — delivered through a trusted supply chain and finished in our Lincolnshire workshop to the highest standards.",
    img: IMG_FAB,
  },
  {
    id: 3,
    tag: "03 — CAD Design",
    title: "Engineered before it's cut.",
    body: "Onshape-based modelling, supported by specialist design partners where required — ensuring every build is thought through, efficient, and ready for manufacture.",
    img: IMG_CAD,
  },
  {
    id: 4,
    tag: "04 — Quality",
    title: "Measured, documented, delivered.",
    body: "Full material traceability, ISO-aligned procedures and PED-compliant sign-off as standard.",
    img: IMG_QA,
  },
];

export const Capabilities = () => {
  const [active, setActive] = useState(0);
  const [hovering, setHovering] = useState(false);
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!sectionRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35 }
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || hovering) return;
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % steps.length);
    }, 3800);
    return () => clearInterval(id);
  }, [inView, hovering]);

  const s = steps[active];

  return (
    <section
      id="capabilities"
      ref={sectionRef}
      data-testid="capabilities-section"
      className="relative bg-[#09090b] py-24 md:py-32 overflow-hidden"
    >
      <EngineeringGrid gridOpacity={0.04} />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20"
        >
          <div>
            <p className="overline mb-5">— What we do</p>
            <h2 className="font-heading text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] max-w-2xl">
              Four disciplines.
              <br />
              One workshop floor.
            </h2>
          </div>
          <p className="max-w-xs text-white/50 font-body text-sm">
            Design, cut, weld, inspect — every project flows through a single
            Lincolnshire workshop under one quality system.
          </p>
        </motion.div>

        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch"
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
        >
          {/* Left: step list */}
          <div className="lg:col-span-5 flex flex-col gap-3">
            {steps.map((step, i) => (
              <button
                key={step.id}
                data-testid={`capability-step-${step.id}`}
                onClick={() => setActive(i)}
                className={`module-card group text-left relative overflow-hidden rounded-2xl border transition-all duration-500 p-6 md:p-7 ${
                  i === active
                    ? "border-white/25 bg-white/[0.04]"
                    : "border-white/10 bg-transparent hover:border-white/20 hover:bg-white/[0.02]"
                }`}
              >
                <span className="reflect" aria-hidden />
                {i === active && (
                  <motion.span
                    layoutId="cap-active-bar"
                    className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#FF5722]"
                    transition={{ type: "spring", stiffness: 320, damping: 30 }}
                  />
                )}
                <div className="flex items-baseline justify-between">
                  <span
                    className={`font-mono text-xs tracking-[0.3em] uppercase transition-colors ${
                      i === active ? "text-[#FF5722]" : "text-white/40"
                    }`}
                  >
                    {step.tag}
                  </span>
                  <span className="font-mono text-[10px] tracking-widest text-white/30">
                    0{i + 1}/0{steps.length}
                  </span>
                </div>
                <h3
                  className={`mt-3 font-heading text-2xl md:text-3xl leading-tight tracking-tight transition-colors ${
                    i === active ? "text-white" : "text-white/60"
                  }`}
                >
                  {step.title}
                </h3>
                <motion.div
                  initial={false}
                  animate={{
                    height: i === active ? "auto" : 0,
                    opacity: i === active ? 1 : 0,
                  }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <p className="mt-4 text-white/60 text-sm md:text-base leading-relaxed font-body max-w-md">
                    {step.body}
                  </p>
                </motion.div>
              </button>
            ))}
          </div>

          {/* Right: image */}
          <div className="lg:col-span-7">
            <div className="relative h-[55vh] md:h-[70vh] rounded-2xl overflow-hidden border border-white/10 bg-black">
              <AnimatePresence mode="wait">
                <motion.img
                  key={s.id}
                  src={s.img}
                  alt={s.title}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid={`capability-image-${s.id}`}
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/10 to-transparent pointer-events-none" />

              <div className="absolute bottom-5 left-5 glass rounded-full px-4 py-2 text-[10px] font-mono tracking-[0.3em] uppercase text-white/80">
                Fina / Capabilities
              </div>

              <div className="absolute bottom-5 right-5 flex gap-1.5">
                {steps.map((st, i) => (
                  <span
                    key={st.id}
                    data-testid={`capability-indicator-${i}`}
                    className={`h-[3px] rounded-full transition-all duration-500 ${
                      i === active ? "w-10 bg-[#FF5722]" : "w-3 bg-white/30"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
